import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  Label,
  useToast,
} from "@egaranti/components";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import extendedWarrantyService from "@/services/extendedWarrantyService";

import SaleStatusTag from "@/components/extendedWarranty/transactions/saleStatusTag";
import CheckRole from "@/components/shared/checkRole";
import { Breadcrumb, BreadcrumbItem } from "@/components/shared/breadcrumbs";

import { SALE_STATUS, getSaleStatus } from "@/lib/saleStatus";

/**
 * Garanti başlangıç tarihi customer-api'de ön biçimlenmemiş `LocalDateTime` —
 * ISO metni olarak geliyor ("2026-08-09T00:00:00"). Ekranda gün/ay/yıl yeterli.
 */
const formatDate = (value) => {
  if (!value) return "-";
  const datePart = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(datePart)
    ? datePart.split("-").reverse().join("-")
    : String(value);
};

const Detail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [data, setData] = useState({});
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const { id } = useParams();

  useEffect(() => {
    extendedWarrantyService.getExtendedWarranty(id).then((res) => {
      setData(res);
    });
  }, []);

  // Satış her adımda iptale çevrilebilir; sadece zaten iptal olan tekrar iptal edilmez.
  const isCancelled = getSaleStatus(data) === SALE_STATUS.CANCELLED;

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await extendedWarrantyService.cancelExtendedWarranty(id);
      toast({
        title: t("extendedWarrantiesPage.cancelSuccess", "Satış iptal edildi"),
        variant: "success",
      });
      navigate("/extended-warranties/transactions");
    } catch (err) {
      toast({
        title:
          err.response?.data?.errors?.[0]?.message ||
          t("extendedWarrantiesPage.cancelError", "Satış iptal edilemedi"),
        variant: "error",
      });
    } finally {
      setCancelling(false);
      setShowCancelDialog(false);
    }
  };

  return (
    <main className="flex min-h-screen flex-col bg-[#F9FAFC]">
      <Breadcrumb>
        <BreadcrumbItem link="/extended-warranties/transactions">
          {t("extendedWarrantiesPage.title")}
        </BreadcrumbItem>
        <BreadcrumbItem active>
          {t("extendedWarrantiesPage.detail")}
        </BreadcrumbItem>
      </Breadcrumb>
      <div className="container mx-auto my-20 flex flex-col items-center justify-center gap-6">
        <div className="formBox grid grid-cols-2 gap-4">
          <div className="col-span-2 mb-5 flex items-center justify-between gap-4">
            <h2 className="text-lg font-medium">
              {t("extendedWarrantiesPage.detail")}
            </h2>
            {/* Kayıt yüklenmeden statü basmıyoruz: boş objede türetme "Taslak" derdi. */}
            {data?.id ? <SaleStatusTag sale={data} /> : null}
          </div>

          <div className="flex flex-col gap-4">
            <Label className="col-span-2">
              {t("extendedWarrantiesPage.firstName")}
            </Label>
            <Input value={data?.firstName} disabled />
          </div>
          <div className="flex flex-col gap-4">
            <Label className="col-span-2">
              {t("extendedWarrantiesPage.lastName")}
            </Label>
            <Input value={data?.lastName} disabled />
          </div>
          <div className="flex flex-col gap-4">
            <Label className="col-span-2">
              {t("extendedWarrantiesPage.email")}
            </Label>
            <Input value={data?.email} disabled />
          </div>
          <div className="flex flex-col gap-4">
            <Label className="col-span-2">
              {t("extendedWarrantiesPage.phone")}
            </Label>
            <Input value={data?.phone} disabled />
          </div>
          <div className="flex flex-col gap-4">
            <Label className="col-span-2">
              {t("extendedWarrantiesPage.productName")}
            </Label>
            <Input value={data?.productName} disabled />
          </div>
          <div className="flex flex-col gap-4">
            <Label className="col-span-2">
              {t("extendedWarrantiesPage.brand")}
            </Label>
            <Input value={data?.brand} disabled />
          </div>
          <div className="flex flex-col gap-4">
            <Label className="col-span-2">
              {t("extendedWarrantiesPage.productPrice")}
            </Label>
            <Input value={data?.productPrice} disabled />
          </div>
          <div className="flex flex-col gap-4">
            <Label className="col-span-2">
              {t("extendedWarrantiesPage.offerPrice")}
            </Label>
            <Input value={data?.offerPrice} disabled />
          </div>
          <div className="flex flex-col gap-4">
            <Label className="col-span-2">
              {t("extendedWarrantiesPage.extendedWarrantyYear")}
            </Label>
            <Input value={data?.extendedWarrantyYear} disabled />
          </div>
          <div className="flex flex-col gap-4">
            <Label className="col-span-2">
              {t("extendedWarrantiesPage.extendedWarrantyExpireDate")}
            </Label>
            <Input value={data?.extendedWarrantyExpireDate} disabled />
          </div>
          <div className="flex flex-col gap-4">
            <Label className="col-span-2">
              {t("extendedWarrantiesPage.externalProductName")}
            </Label>
            <Input value={data?.externalProductName} disabled />
          </div>
          {/* EPS-4970 revize: "Satış Ekle" formunda girilen alanlar. customer-api
              bunları zaten dönüyordu; gateway DTO'sunda mirror edilmediği için
              panele hiç ulaşmıyorlardı. */}
          <div className="flex flex-col gap-4">
            <Label className="col-span-2">
              {t(
                "extendedWarrantiesPage.identificationNumber",
                "TCKN / VKN",
              )}
            </Label>
            <Input value={data?.identificationNumber || "-"} disabled />
          </div>
          <div className="flex flex-col gap-4">
            <Label className="col-span-2">
              {t("extendedWarrantiesPage.invoiceNumber", "Fatura Numarası")}
            </Label>
            <Input value={data?.invoiceNumber || "-"} disabled />
          </div>
          {data?.saleInvoiceUrl && (
            <div className="flex flex-col gap-4">
              <Label className="col-span-2">
                {t("extendedWarranty.detail.saleInvoice", "Satış Faturası")}
              </Label>
              <a
                href={data.saleInvoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-800"
              >
                {t("extendedWarranty.detail.saleInvoiceView", "Görüntüle")}
              </a>
            </div>
          )}
          <div className="flex flex-col gap-4">
            <Label className="col-span-2">
              {t(
                "extendedWarrantiesPage.warrantyStartDate",
                "Garanti Başlangıç Tarihi",
              )}
            </Label>
            <Input value={formatDate(data?.warrantyStartDate)} disabled />
          </div>
          {/* EPS-5097: Satış Ekle'de girilen personel (extended_warranty.personal_name);
              entegrasyon/Excel satışlarında oradaki satış personeli. */}
          <div className="flex flex-col gap-4">
            <Label className="col-span-2">
              {t(
                "extendedWarrantiesPage.salesPersonnel",
                "Satışı Yapan Personel",
              )}
            </Label>
            <Input value={data?.personalName || "-"} disabled />
          </div>
          {/* EPS-5097: müşteriye "poliçe" değil "sözleşme" diyoruz (ek garanti sigorta
              değil, hizmet sözleşmesi). Alan adları (policies) BE sözleşmesi. */}
          <div className="flex flex-col gap-4">
            <Label className="col-span-2">
              {t("extendedWarrantiesPage.contracts", "Sözleşmeler")}
            </Label>
            {data?.policies === null ? (
              <div className="italic text-gray-500">
                {t(
                  "extendedWarrantiesPage.contractNotCreated",
                  "Sözleşme oluşmamış",
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {data?.policies?.map((policy, index) => (
                  <a
                    key={index}
                    href={policy.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:text-blue-800"
                  >
                    {policy.packageName}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {data?.id && !isCancelled && (
          <CheckRole section="ACTIVATIONS_SALES" action="EDIT">
            <div className="flex w-full justify-end">
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={() => setShowCancelDialog(true)}
              >
                {t("extendedWarrantiesPage.cancelSale", "Satışı İptal Et")}
              </Button>
            </div>
          </CheckRole>
        )}
      </div>

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("extendedWarrantiesPage.cancelSale", "Satışı İptal Et")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "extendedWarrantiesPage.cancelSaleWarning",
                "Satış iptal edilecek ve varsa sözleşmesi iptal edilecek. Bu işlem para iadesi yapmaz ve geri alınamaz.",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", "Vazgeç")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={cancelling}
              className="bg-red-600 hover:bg-red-700"
              onClick={handleCancel}
            >
              {t("extendedWarrantiesPage.cancelSaleConfirm", "Satışı İptal Et")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
};

export default Detail;
